/**
 * Barrel export + dispatcher for the TYPED per-kind render adapters
 * (DEFUSE S3; LIVE since the S11 render cutover).
 *
 * Typed counterpart of `../index.ts`'s `entityToRenderable`. Where the legacy
 * dispatcher takes an `EntityPreview` and the adapters read the
 * `EntityPreview.properties` string bag, this dispatcher takes the canonical
 * `DrawingEntity` / `BlockChildEntity` and routes to the typed adapters that
 * read the model fields directly (`start`/`end`, `center`/`radius`, …).
 *
 * The migrated kinds (S3: Line / Point / Circle / Arc / Ellipse; S4–S7: Text /
 * MText / LWPOLYLINE / Spline / Wipeout / Revision Cloud / Hatch) have a typed
 * adapter — enumerated in {@link TYPED_RENDER_KINDS}; every other kind returns
 * `null`, mirroring the legacy dispatcher's `null` for not-yet-supported
 * kinds. The caller then falls back to the legacy bag path for exactly the
 * kinds in `BAG_FALLBACK_RENDER_KINDS` (see `../typedFirstRenderable.ts` —
 * the census test pins that set so it can only shrink).
 *
 * DEFUSE S11 — this dispatcher IS the live render path: the scene lift
 * pipeline (`state/spatialBuilder.ts`, `scene/SceneBulkLoad.ts`,
 * `scene/StagePointerHandlers.ts`) resolves the typed `DrawingEntity` via
 * `getEntityById` and dispatches here first, WITHOUT the
 * `buildShellEntityPreview` string-bag round-trip (plan §4.3).
 */

import type {
  DrawingEntity,
  BlockChildEntity,
} from "@dxf-ai-editor/drawing-model";
import type { EntityType } from "@dxf-ai-editor/contracts";

import type { RenderableEntity } from "../../types";

import type { AdapterContext } from "../adapterContext";

import { lineFromEntity } from "./lineFromEntity";
import { pointFromEntity } from "./pointFromEntity";
import { circleFromEntity } from "./circleFromEntity";
import { arcFromEntity } from "./arcFromEntity";
import { ellipseFromEntity } from "./ellipseFromEntity";
import { textFromEntity } from "./textFromEntity";
import { mtextFromEntity } from "./mtextFromEntity";
import { lwpolylineFromEntity } from "./lwpolylineFromEntity";
import { splineFromEntity } from "./splineFromEntity";
import { wipeoutFromEntity } from "./wipeoutFromEntity";
import { revisionCloudFromEntity } from "./revisionCloudFromEntity";
import { hatchFromEntity } from "./hatchFromEntity";
import { dimensionFromEntity } from "./dimensionFromEntity";
import { polylineFromEntity } from "./polylineFromEntity";
import { oleFrameFromEntity } from "./oleFrameFromEntity";

export { lineFromEntity } from "./lineFromEntity";
export { pointFromEntity } from "./pointFromEntity";
export { circleFromEntity } from "./circleFromEntity";
export { arcFromEntity } from "./arcFromEntity";
export { ellipseFromEntity } from "./ellipseFromEntity";
export { textFromEntity } from "./textFromEntity";
export { mtextFromEntity } from "./mtextFromEntity";
export { lwpolylineFromEntity } from "./lwpolylineFromEntity";
export { splineFromEntity } from "./splineFromEntity";
export { wipeoutFromEntity } from "./wipeoutFromEntity";
export { revisionCloudFromEntity } from "./revisionCloudFromEntity";
export { hatchFromEntity } from "./hatchFromEntity";
export { dimensionFromEntity } from "./dimensionFromEntity";
export { polylineFromEntity } from "./polylineFromEntity";
export { oleFrameFromEntity } from "./oleFrameFromEntity";
// `tableFromEntity` is NOT re-exported from here and is NOT a switch case
// below — like `multileaderFromEntity`, it needs the block-content flatten
// INJECTED, and importing that here would close an import cycle through
// `blockFromEntity.ts`. `typedFirstRenderable.ts` dispatches it explicitly.
// `multileaderFromEntity` is NOT re-exported from here — it is deliberately
// NOT part of the `entityToRenderableTyped` switch below (see its own
// module doc: importing it here would close an import cycle through
// `blockFromEntity.ts`, same constraint documented on that file for
// "Block"). Callers needing it import `./multileaderFromEntity` directly.

/**
 * DEFUSE S11 — census of the kinds this dispatcher covers with a REAL typed
 * adapter (the switch cases below, one-to-one). Single source of truth for
 * the typed-first routing in `../typedFirstRenderable.ts`: a kind listed here
 * renders through the typed path; a kind NOT listed here falls back to the
 * legacy bag pipeline. The census test
 * (`../__tests__/typedFirstRenderable.census.test.ts`) asserts this set plus
 * the fallback set exactly partitions `entityTypeSchema` — growing the
 * dispatcher without moving the kind out of the fallback set fails the gate.
 */
export const TYPED_RENDER_KINDS: ReadonlySet<EntityType> = new Set<EntityType>([
  "Line",
  "Point",
  "Circle",
  "Arc",
  "Ellipse",
  "Text",
  "MText",
  "LWPOLYLINE",
  "Spline",
  "Wipeout",
  "Revision Cloud",
  "Hatch",
  "Dimension",
  // Legacy heavy POLYLINE and OLE2FRAME both produce a single renderable from
  // the entity's own fields, so they fit this dispatcher. ACAD_TABLE does NOT
  // (one table → many block-content children, plus an injected flattener), so
  // it lives in EXPLICIT_DISPATCH_RENDER_KINDS alongside Multileader.
  "Polyline",
  "OLE Frame",
]);

/**
 * Typed top-level dispatcher. Switches on `entity.type` and delegates to the
 * matching typed adapter. Returns `null` for every kind that does not yet have
 * a typed adapter (mirrors the legacy `entityToRenderable` returning `null`
 * for unsupported kinds) — the caller skips and the entity stays out of the
 * spatial index until the relevant follow-up slice migrates it.
 *
 * Accepts both top-level `DrawingEntity` and `BlockChildEntity` (a block
 * child's geometry is the same discriminated union for the migrated kinds), so
 * the future block-flatten path can reuse one dispatcher — identical to the
 * legacy adapter's reach.
 */
export function entityToRenderableTyped(
  entity: DrawingEntity | BlockChildEntity,
  ctx: AdapterContext,
): RenderableEntity | null {
  switch (entity.type) {
    case "Line":
      return lineFromEntity(entity, ctx);
    case "Point":
      return pointFromEntity(entity, ctx);
    case "Circle":
      return circleFromEntity(entity, ctx);
    case "Arc":
      return arcFromEntity(entity, ctx);
    case "Ellipse":
      return ellipseFromEntity(entity, ctx);
    case "Text":
      return textFromEntity(entity, ctx);
    case "MText":
      return mtextFromEntity(entity, ctx);
    case "LWPOLYLINE":
      return lwpolylineFromEntity(entity, ctx);
    case "Spline":
      return splineFromEntity(entity, ctx);
    case "Wipeout":
      return wipeoutFromEntity(entity, ctx);
    case "Revision Cloud":
      return revisionCloudFromEntity(entity, ctx);
    case "Hatch":
      return hatchFromEntity(entity, ctx);
    case "Dimension":
      return dimensionFromEntity(entity, ctx);
    case "Polyline":
      return polylineFromEntity(entity, ctx);
    case "OLE Frame":
      return oleFrameFromEntity(entity, ctx);
    // Multileader has a typed adapter (`./multileaderFromEntity.ts`) but is
    // NOT dispatched here — see that module's doc comment (adding the edge
    // `typed/index.ts` → multileader would re-close the import cycle through
    // `blockFromEntity.ts`, which now imports the multileader adapter for its
    // block-child arm). Dispatched explicitly by `typedFirstRenderable.ts` /
    // `scene/SceneBulkLoad.ts` for a TOP-LEVEL MLEADER, and by
    // `./blockChildMultileader.ts` for a block-child one (Codex PR #784
    // round 4 F1 — that child used to be dropped here).
    //
    // Not-yet-migrated kinds (Block, Rectangular Associative Array,
    // Viewport, ATTDEF) — no typed adapter yet; return null exactly like
    // the legacy dispatcher does for kinds it cannot handle. Migrated in
    // later DEFUSE slices.
    default:
      return null;
  }
}
